import { useEffect, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Play, LockSimple, ArrowSquareOut, FileArrowDown, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STATUS_COLOR = {
  draft: "bg-zinc-100 text-zinc-700",
  computing: "bg-amber-100 text-amber-800",
  computed: "bg-blue-100 text-blue-800",
  finalised: "bg-purple-100 text-purple-800",
  published: "bg-emerald-100 text-emerald-800",
};

export default function PayrollRuns() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(null);     // selected run id → show payslips
  const [slips, setSlips] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightErr, setPreflightErr] = useState("");
  const [finaliseRun, setFinaliseRun] = useState(null);
  const [bankCat, setBankCat] = useState("");

  const load = async () => {
    try { const r = await api.get("/payroll-runs"); setRows(r.data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Preflight when user types a month
  const runPreflight = async (ym) => {
    if (!/^\d{4}-\d{2}$/.test(ym || "")) { setPreflight(null); return; }
    setPreflightBusy(true);
    try {
      const r = await api.get(`/payroll/preflight?period_month=${ym}`);
      setPreflight(r.data); setPreflightErr("");
    } catch (e) { setPreflight(null); setPreflightErr(formatApiError(e?.response?.data?.detail)); }
    finally { setPreflightBusy(false); }
  };
  useEffect(() => { runPreflight(month); }, [month]);

  const create = async () => {
    try {
      await api.post("/payroll-runs", { period_month: month, notes });
      toast.success("Run created"); setOpen(false); setMonth(""); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const act = async (rid, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const r = await api.post(`/payroll-runs/${rid}/${path}`);
      toast.success(`Run ${r.data.status}`); load(); if (active === rid) loadSlips(rid);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const loadSlips = async (rid) => {
    setActive(rid);
    try { const r = await api.get(`/payslips?run_id=${rid}`); setSlips(r.data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const download = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const totalGross = rows.reduce((s, r) => s + (r.total_gross || 0), 0);
  const totalNet = rows.reduce((s, r) => s + (r.total_net || 0), 0);

  return (
    <AppShell title="Payroll runs">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total runs" value={rows.length} testid="stat-total-runs"/>
        <StatCard label="Cumulative gross" value={inr(totalGross)} testid="stat-total-gross"/>
        <StatCard label="Cumulative net" value={inr(totalNet)} testid="stat-total-net"/>
        <StatCard label="Published" value={rows.filter(r=>r.status==="published").length} hint="Visible to employees" testid="stat-published"/>
      </div>

      <SectionCard
        title="Monthly cycles"
        subtitle="Draft → Compute → Finalise → Publish. Each cycle pro-rates salaries by LOP from approved leaves."
        testid="section-runs"
        action={
          <Button size="sm" onClick={()=>setOpen(true)} data-testid="create-run-btn" className="gap-1.5">
            <Plus size={14} weight="bold"/> New run
          </Button>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Working days</TableHead>
              <TableHead className="text-right">Employees</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right w-72">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8">No runs yet — click "New run" to create one.</TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`run-row-${r.period_month}`}>
                <TableCell className="font-medium">{r.period_label}</TableCell>
                <TableCell><Badge className={STATUS_COLOR[r.status] || ""}>{r.status}</Badge></TableCell>
                <TableCell className="text-right tabular-nums">{r.working_days}</TableCell>
                <TableCell className="text-right tabular-nums">{r.total_employees}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.total_gross)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{inr(r.total_net)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end flex-wrap">
                    <Button size="sm" variant="outline" onClick={()=>loadSlips(r.id)} data-testid={`view-slips-${r.period_month}`}>View slips</Button>
                    {(r.status === "draft" || r.status === "computed") && (
                      <Button size="sm" onClick={()=>act(r.id, "compute")} data-testid={`compute-${r.period_month}`} className="gap-1">
                        <Play size={12} weight="fill"/> Compute
                      </Button>
                    )}
                    {r.status === "computed" && (
                      <Button size="sm" variant="secondary" onClick={()=>setFinaliseRun(r)} data-testid={`finalise-${r.period_month}`}>Finalise</Button>
                    )}
                    {r.status === "finalised" && (
                      <Button size="sm" onClick={()=>act(r.id, "publish", "Publish payslips to employees?")} data-testid={`publish-${r.period_month}`} className="gap-1">
                        <LockSimple size={12} weight="fill"/> Publish
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {active && (
        <SectionCard title={`Payslips — ${rows.find(r => r.id === active)?.period_label}`}
          subtitle="Click Download for an individual PDF payslip."
          testid="section-payslips"
          action={
            <div className="flex gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" className="gap-1"
                onClick={()=>download(`/payroll-runs/${active}/exports/bank-advice${bankCat ? `?category=${bankCat}` : ""}`, `bank_advice_${bankCat || "all"}_${active}.csv`)}
                data-testid="dl-bank"><FileArrowDown size={14}/> Bank advice</Button>
              <select value={bankCat} onChange={e=>setBankCat(e.target.value)} aria-label="Payment category"
                className="h-8 rounded-md border border-zinc-200 px-1.5 text-xs" data-testid="dl-bank-category">
                <option value="">All employees</option>
                <option value="bank">Bank transfer (not cash)</option>
                <option value="idbi">IDBI Bank</option>
                <option value="non_idbi">Non IDBI Bank</option>
                <option value="cash">Cash</option>
                <option value="fix_cash">Fix Cash</option>
                <option value="under_18">Under 18</option>
              </select>
              <Button size="sm" variant="outline" className="gap-1"
                onClick={()=>download(`/payroll-runs/${active}/exports/form-24q`, `form_24q_${active}.csv`)}
                data-testid="dl-24q"><FileArrowDown size={14}/> Form 24Q</Button>
              <Button size="sm" variant="outline" className="gap-1"
                onClick={()=>download(`/payroll-runs/${active}/exports/pf-ecr`, `ECR_${active}.txt`)}
                data-testid="dl-pfecr"><FileArrowDown size={14}/> PF ECR</Button>
              <Button size="sm" variant="outline" className="gap-1"
                onClick={()=>download(`/payroll-runs/${active}/exports/esic-monthly`, `esic_${active}.csv`)}
                data-testid="dl-esic"><FileArrowDown size={14}/> ESIC</Button>
            </div>
          }
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Paid days</TableHead>
                <TableHead className="text-right">LOP</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net pay</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slips.map(s => (
                <TableRow key={s.id}>
                  <TableCell><div className="font-medium">{s.employee_name}</div><div className="text-xs text-zinc-500">{s.employee_code}</div></TableCell>
                  <TableCell className="text-right tabular-nums">{s.paid_days}/{s.working_days}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.lop_days}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(s.actual_gross)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(s.total_deductions)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{inr(s.actual_net)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="gap-1"
                      onClick={()=>download(`/payslips/${s.id}/pdf`, `payslip_${s.period_month}_${s.employee_code}.pdf`)}
                      data-testid={`dl-slip-${s.employee_code}`}>
                      <DownloadSimple size={14}/> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="create-run-dialog">
          <DialogHeader><DialogTitle>New payroll run</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Period (YYYY-MM)</Label>
              <Input value={month} onChange={e=>setMonth(e.target.value)} placeholder="2026-04" data-testid="run-month-input"/>
              <p className="text-xs text-zinc-500 mt-1">Working days and LOP will be auto-calculated for this month.</p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g., Q4 bonus not included" data-testid="run-notes-input"/>
            </div>

            {preflightBusy && <div className="text-xs text-zinc-500">Checking readiness…</div>}
            {preflightErr && <div className="text-xs text-rose-600" data-testid="preflight-error">{preflightErr}</div>}
            {preflight && (
              <div className="mt-2 rounded-md border p-3 bg-zinc-50" data-testid="run-preflight">
                <div className="text-sm font-medium mb-2">Pre-run readiness · {preflight.period_month}</div>
                <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                  <StatMini label="Working days" value={preflight.working_days}/>
                  <StatMini label="Active employees" value={preflight.active_employees}/>
                  <StatMini label="With CTC" value={preflight.with_ctc}/>
                  <StatMini label="IT decl." value={`${preflight.declarations_submitted}/${preflight.active_employees}`}/>
                </div>

                {(preflight.blockers || []).map((b, i) => (
                  <div key={i} className="rounded border border-rose-300 bg-rose-50 p-2 text-xs mb-1.5">
                    <div className="font-medium text-rose-800">🚫 {b.message}</div>
                    {(b.sample || []).length > 0 && (
                      <div className="text-rose-700 mt-1">
                        e.g. {b.sample.map(s => s.name).join(", ")}{b.count > (b.sample?.length||0) ? ` +${b.count - b.sample.length} more` : ""}
                      </div>
                    )}
                  </div>
                ))}
                {(preflight.warnings || []).map((w, i) => (
                  <div key={i} className="rounded border border-amber-300 bg-amber-50 p-2 text-xs mb-1.5">
                    <span className="font-medium text-amber-900">⚠️ {w.message}</span>
                  </div>
                ))}
                {(preflight.blockers.length === 0 && preflight.warnings.length === 0) && (
                  <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800">
                    ✅ All checks passed — safe to compute.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="run-submit-btn"
              disabled={preflight && preflight.blockers && preflight.blockers.length > 0}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    <Dialog open={!!finaliseRun} onOpenChange={(o)=>!o && setFinaliseRun(null)}>
        <DialogContent data-testid="finalise-confirm-dialog">
          <DialogHeader><DialogTitle>Finalise {finaliseRun?.period_label} payroll?</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-600 space-y-2">
            <p>{finaliseRun?.total_employees} payslips · gross {inr(finaliseRun?.total_gross)} · net {inr(finaliseRun?.total_net)}.</p>
            <p>Once finalised, payslips are locked and loan instalments are marked paid. Only a super admin can reopen.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setFinaliseRun(null)} data-testid="finalise-cancel">Cancel</Button>
            <Button onClick={()=>{ const r = finaliseRun; setFinaliseRun(null); act(r.id, "finalise"); }} data-testid="finalise-confirm">Yes, finalise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="rounded bg-white border border-zinc-200 p-2">
      <div className="text-[10px] uppercase text-zinc-500">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
